import { useEffect, useRef, useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { FEATURE_FLAGS, flagEnabled, refreshAuth, useAuth, type AuthUser } from "../../hooks/useAuth";
import { getCsrfHeader } from "../../lib/csrf";
import { isAppShell } from "../../lib/apiError";
import { prepareProfileAvatar, type ProfileAvatarUpload } from "../../lib/profileAvatar";
import { ProfileAvatar } from "../../components/dashboard/top-nav/ProfileAvatar";
import { Skeleton } from "../../components/dashboard/Skeleton";
import { useCachedQuery } from "../../hooks/useCachedQuery";
import { adaptOwner } from "./dashboardAdapters";
import styles from "./UserProfile.module.css";

type SyncTarget = {
  tenantId: string;
  name: string;
  status: "not_saved" | "pending" | "synced" | "failed" | "paused";
  lastSyncedAt: string | null;
  errorMessage: string | null;
};
type Profile = { displayName: string | null; avatarUrl: string | null; version: number; syncTargets: SyncTarget[] };

class ProfileError extends Error {
  constructor(message: string, readonly status: number) { super(message); }
}

const profileRequest = async (suffix = "", init: RequestInit = {}): Promise<Profile> => {
  const response = await fetch(`/api/me/profile${suffix}`, { ...init, credentials: "include" });
  if (isAppShell(response)) throw new ProfileError("Profile editing is unavailable for this account.", 403);
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { message?: string } | null;
    throw new ProfileError(body?.message || "Could not load your profile. Please try again.", response.status);
  }
  return await response.json() as Profile;
};

const ProfileLoadingCard = (): JSX.Element => <div className={styles.card} aria-label="Loading profile">
    <div className={styles.photoRow}><Skeleton width="80px" height="80px" radius="50%" /><Skeleton width="120px" height="34px" /></div>
    <Skeleton width="100%" height="48px" /><Skeleton width="100%" height="48px" />
</div>;
const ProfileSkeleton = (): JSX.Element => <div className={styles.page}>
  <header className={styles.header}><Skeleton width="220px" height="42px" /><Skeleton width="480px" height="22px" /></header>
  <ProfileLoadingCard />
</div>;

export const UserProfile = (): JSX.Element => {
  const { user, isRevalidating } = useAuth();
  if (!user || isRevalidating) return <ProfileSkeleton />;
  if (!flagEnabled(user.features, FEATURE_FLAGS.userProfile)) return <Navigate to="/dashboard/overview" replace />;
  return <ProfileForm key={user.email} user={user} />;
};

const ProfileForm = ({ user }: { readonly user: AuthUser }): JSX.Element => {
  const navigate = useNavigate();
  const owner = adaptOwner(user);
  const fallbackName = `${owner.firstName} ${owner.lastName}`.trim();
  const [saved, setSaved] = useState<Profile | null>(null);
  const [name, setName] = useState("");
  const [avatar, setAvatar] = useState<ProfileAvatarUpload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const selection = useRef(0);
  const initialized = useRef(false);

  const profileQuery = useCachedQuery<Profile>({
    cacheResource: "profile:self",
    queryKey: "/api/me/profile",
    fetcher: (signal) => profileRequest("", { signal }),
    refreshIntervalMs: polling ? 5000 : 0,
  });

  const accept = (profile: Profile, updateCache = true): void => {
    setSaved(profile); setName(profile.displayName ?? fallbackName);
    setAvatar(null); setConflict(false);
    if (updateCache) profileQuery.mutate(() => profile);
  };
  const showError = (err: unknown): void => {
    if (err instanceof ProfileError && err.status === 403) {
      void refreshAuth();
      navigate("/dashboard/overview", { replace: true });
      return;
    }
    setConflict(err instanceof ProfileError && err.status === 409);
    setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
  };

  useEffect(() => {
    setPolling(profileQuery.data?.syncTargets.some((target) => target.status === "pending") ?? false);
    if (!initialized.current && profileQuery.data) {
      initialized.current = true;
      accept(profileQuery.data, false);
    }
  }, [profileQuery.data]);

  useEffect(() => () => { selection.current++; }, []);

  const targets = profileQuery.data?.syncTargets ?? saved?.syncTargets ?? [];
  const pending = targets.some((target) => target.status === "pending");

  const upload = async (file: File): Promise<void> => {
    const current = ++selection.current;
    setPreparing(true); setError(null); setNotice(null);
    try {
      const result = await prepareProfileAvatar(file);
      if (current === selection.current) setAvatar(result);
    } catch (err) { if (current === selection.current) showError(err); }
    finally { if (current === selection.current) setPreparing(false); }
  };

  const save = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!saved || busy || preparing) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const profile = await profileRequest("", { method: "PATCH", headers: { "Content-Type": "application/json", ...getCsrfHeader() },
        body: JSON.stringify({ displayName: name.trim(), version: saved.version, ...(avatar ? { avatar } : {}) }) });
      accept(profile);
      setNotice("Your profile is saved.");
      await refreshAuth();
    } catch (err) { showError(err); }
    finally { setBusy(false); }
  };

  const retry = async (): Promise<void> => {
    setBusy(true); setError(null);
    try {
      const profile = await profileRequest("/sync", { method: "POST", headers: getCsrfHeader() });
      profileQuery.mutate(() => profile);
      setSaved((current) => current ? { ...current, syncTargets: profile.syncTargets } : current);
    } catch (err) { showError(err); }
    finally { setBusy(false); }
  };

  const reload = async (): Promise<void> => {
    setBusy(true); setError(null);
    try { accept(await profileRequest()); } catch (err) { showError(err); }
    finally { setBusy(false); }
  };

  const preview = avatar ? `data:${avatar.mime};base64,${avatar.dataBase64}` : saved?.avatarUrl;
  const initials = name.trim().split(/\s+/).filter(Boolean).map((part) => part[0]).filter(Boolean);
  const avatarInitials = (initials.length > 1 ? `${initials[0]}${initials[initials.length - 1]}` : initials[0] ?? "?").toUpperCase();
  const dirty = saved !== null && (name.trim() !== (saved.displayName ?? fallbackName) || avatar !== null || saved.version === 0);

  return <div className={styles.page}>
    <header className={styles.header}><h1>Your profile</h1><p>Choose how you appear in ZeroToClaw and your connected instances.</p></header>
    {error && <div className={styles.error} role="alert">{error}
      {(conflict || !saved) && <button type="button" onClick={() => void reload()} disabled={busy}>Reload saved profile</button>}
    </div>}
    {!saved && !error && profileQuery.status === "error" && <div className={styles.error} role="alert">{profileQuery.error ?? "Could not load your profile."}
      <button type="button" onClick={() => void profileQuery.refetch()}>Reload saved profile</button>
    </div>}
    {!saved && !error && profileQuery.status === "loading" && <ProfileLoadingCard />}
    {saved && <>
      <form className={styles.card} onSubmit={(event) => void save(event)}>
        <div className={styles.photoRow}>
          <div className={styles.avatar} data-testid="profile-photo"><ProfileAvatar url={preview} initials={avatarInitials} /></div>
          <div><label className={styles.upload}>Upload photo
            <input type="file" accept="image/jpeg,image/png,image/webp" aria-label="Profile picture" disabled={busy || preparing}
              onChange={(event) => { const file = event.target.files?.[0]; if (file) void upload(file); event.target.value = ""; }} />
          </label><p className={styles.hint}>{preparing ? "Preparing photo…" : "JPEG, PNG, or WebP · up to 10 MB. Preview shows the center crop."}</p></div>
        </div>
        <label className={styles.field}>Display name
          <input value={name} onChange={(event) => { setName(event.target.value); setNotice(null); }} required maxLength={256} disabled={busy} autoComplete="name" />
        </label>
        <label className={styles.field}>Email<input value={user.email} readOnly type="email" /></label>
        <div className={styles.actions}>
          <button className={styles.primary} type="submit" disabled={busy || preparing || !dirty || !name.trim()}>{busy ? "Saving…" : "Save changes"}</button>
          <button type="button" disabled={busy || preparing} onClick={() => navigate("/dashboard/overview")}>Cancel</button>
        </div>
      </form>
      <section className={styles.card} aria-label="Profile sync">
        <h2>Connected instances</h2>
        <p className={styles.syncStatus} role="status" aria-live="polite">{notice ? `${notice} ` : ""}{pending ? "Syncing to your instances…" : saved.version === 0 ? "Your changes will sync here when you save." : targets.every((target) => target.status === "synced") ? "Your profile is up to date on all connected instances." : "Some instances are waiting for your profile."}</p>
        <ul className={styles.targets}>{targets.map((target) => <li key={target.tenantId}>
          <span>{target.name}</span><span className={styles.targetStatus}>{target.status === "synced" ? "Up to date" : target.status === "failed" ? "Needs attention" : target.status === "not_saved" ? "Ready" : "Pending"}
            {target.errorMessage && <small>{target.errorMessage}</small>}
          </span>
        </li>)}</ul>
        {targets.some((target) => target.status === "failed" || (target.status === "pending" && target.errorMessage)) &&
          <button type="button" disabled={busy} onClick={() => void retry()}>Retry sync</button>}
      </section>
    </>}
  </div>;
};
